import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketingApi, type LifecyclePreset, type MarketingSegment } from '@/api/marketing.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function MarketingLifecyclePage() {
  const [presets, setPresets] = useState<LifecyclePreset[]>([]);
  const [segments, setSegments] = useState<MarketingSegment[]>([]);
  const [segmentId, setSegmentId] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        marketingApi.listLifecyclePresets(),
        marketingApi.listSegments(),
      ]);
      setPresets(p);
      setSegments(s);
    } catch (err) {
      showApiError(err, 'Failed to load lifecycle presets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (code: string) => {
    try {
      const campaign = await marketingApi.createLifecycleCampaign(
        code,
        segmentId === '' ? undefined : Number(segmentId)
      );
      notify.success(`Draft created: ${campaign.campaignName}`);
    } catch (err) {
      showApiError(err, 'Failed to create campaign');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lifecycle"
        description="One-click drafts for welcome, cart recovery, win-back, upsell, and renewal programs."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Marketing', to: '/marketing' },
          { label: 'Lifecycle' },
        ]}
      />

      <div className="card p-4 flex flex-wrap gap-4 items-end">
        <div className="min-w-[240px] flex-1">
          <Select
            label="Audience (optional)"
            placeholder="Use default rules"
            options={segments.map((s) => ({ value: String(s.id), label: s.segmentName }))}
            value={segmentId === '' ? '' : String(segmentId)}
            onChange={(v) => setSegmentId(v ? Number(v) : '')}
          />
        </div>
        <Link to="/marketing/campaigns" className="text-sm text-violet-600 hover:underline">
          Manage campaigns →
        </Link>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading presets…</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {presets.map((p) => (
            <article key={p.code} className="card p-5 flex flex-col">
              <h3 className="font-medium text-slate-800">{p.name}</h3>
              <p className="text-xs text-slate-500 mt-1">{p.channel}</p>
              <Button className="mt-4 w-full" variant="secondary" onClick={() => void create(p.code)}>
                Create draft
              </Button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

